import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { mediaUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ReadAlong, TranscriptList, useActiveCue } from "../components/ReadAlong";
import UpsellSheet from "../components/UpsellSheet";
import { useBackdrop } from "../lib/backdrops";
import { ChevronLeft, Play, Pause, Heart, FileText, X, Loader2, RotateCcw, Info } from "lucide-react";

const fmt = (s) => {
  if (!s || isNaN(s)) return "0:00";
  const m = Math.floor(s / 60); const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, "0")}`;
};

export default function Player() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const audioRef = useRef(null);
  const [session, setSession] = useState(null);
  const [preparing, setPreparing] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);
  const [hearted, setHearted] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showUpsell, setShowUpsell] = useState(false);
  const lowCredits = (user?.credits ?? 99) <= 1;

  const leave = () => { if (lowCredits) setShowUpsell(true); else navigate("/library"); };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/sessions/${id}`);
        if (!alive) return;
        setSession(data); setHearted(data.hearted); setDur(data.duration || 0);
        await api.post(`/sessions/${id}/prepare`);
        if (!alive) return;
        if (!data.transcript_cues?.length) {
          const { data: fresh } = await api.get(`/sessions/${id}`);
          if (alive) setSession(fresh);
        }
        setPreparing(false);
      } catch (e) {
        toast.error("Could not load session");
        navigate("/library");
      }
    })();
    return () => { alive = false; };
  }, [id, navigate]);

  const toggle = () => {
    const el = audioRef.current;
    if (!el) return;
    if (playing) { el.pause(); } else { el.play().catch(() => toast.error("Tap play again to start audio")); }
  };

  const seek = (v) => { if (audioRef.current) { audioRef.current.currentTime = v; setCur(v); } };
  const restart = () => seek(0);

  const heart = async () => {
    const next = !hearted; setHearted(next);
    try { await api.post(`/sessions/${id}/feedback`, { hearted: next }); } catch (e) { /* ignore */ }
  };

  const sendFeedback = async (rating) => {
    try { await api.post(`/sessions/${id}/feedback`, { completed: true, rating }); } catch (e) { /* ignore */ }
    setShowFeedback(false);
    toast.success("Thanks — this makes your next session better");
    leave();
  };

  const pct = dur ? (cur / dur) * 100 : 0;
  const cues = session?.transcript_cues;
  const active = useActiveCue(cues, cur);
  const backdrop = useBackdrop(id);
  const started = playing || cur > 0.5;

  return (
    <div className="mm-frame relative bg-navy text-white flex flex-col overflow-hidden" data-testid="player-page">
      <img src={session?.type === "visualization" ? backdrop.file : "/dream-backdrop.jpg"} alt="" className="absolute inset-0 w-full h-full object-cover" style={session?.type === "visualization" ? { objectPosition: backdrop.pos } : undefined} />
      <div className="absolute inset-0 mm-player-veil" />
      {session?.type === "visualization" && <div className="absolute inset-0 bg-navy/45" />}

      <audio ref={audioRef} src={mediaUrl(id)} preload="auto"
        onLoadedMetadata={(e) => { if (e.target.duration && isFinite(e.target.duration)) setDur(e.target.duration); }}
        onTimeUpdate={(e) => setCur(e.target.currentTime)}
        onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)}
        onEnded={() => { setPlaying(false); setShowFeedback(true); }} />

      <div className="relative z-10 flex items-center justify-between px-5 pt-6">
        <button data-testid="player-back-btn" onClick={() => navigate(-1)} className="w-10 h-10 rounded-full bg-white/15 backdrop-blur flex items-center justify-center">
          <ChevronLeft size={22} />
        </button>
        <div className="text-center min-w-0 px-2">
          <div className="text-turq text-[10px] font-bold uppercase tracking-[0.2em]">{session?.category}</div>
          <div className="font-serif text-base font-bold truncate">{session?.title || "Loading…"}</div>
        </div>
        <button data-testid="player-heart-btn" onClick={heart} className="w-10 h-10 rounded-full bg-white/15 backdrop-blur flex items-center justify-center">
          <Heart size={20} className={hearted ? "fill-rose-500 text-rose-500" : "text-white"} />
        </button>
      </div>

      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 text-center">
        {cues?.length ? (
          <ReadAlong cues={cues} active={active} started={started} />
        ) : (
          <>
            <div className="w-3 h-3 rounded-full bg-turq mb-6 animate-pulse" />
            <h1 className="font-serif text-3xl font-bold leading-tight">{session?.title || "Loading…"}</h1>
            <p className="text-white/60 mt-2 text-sm capitalize">{session?.type} · {session?.guide?.replace("_", " ")}</p>
          </>
        )}
        {session?.narration_fallback && (
          <div className="mt-4 flex items-start gap-2 bg-white/10 border border-white/15 rounded-xl px-3 py-2 text-left text-[11px] text-white/70" data-testid="narration-fallback-note">
            <Info size={14} className="shrink-0 mt-0.5" />
            <span>Voice narration is temporarily unavailable. A calming tone is playing instead.</span>
          </div>
        )}
      </div>

      <div className="relative z-10 px-6 pb-[max(2rem,env(safe-area-inset-bottom))]">
        <button data-testid="player-transcript-btn" onClick={() => setShowTranscript(true)}
          className="mx-auto mb-6 flex items-center gap-2 text-white/70 text-sm font-medium">
          <FileText size={16} /> Transcript
        </button>

        <input data-testid="player-scrubber" type="range" min={0} max={dur || 0} value={cur} step={0.1}
          onChange={(e) => seek(parseFloat(e.target.value))}
          className="w-full h-1.5 appearance-none rounded-full cursor-pointer"
          style={{ background: `linear-gradient(to right, #06B6D4 ${pct}%, rgba(255,255,255,0.25) ${pct}%)` }} />
        <div className="flex justify-between text-xs text-white/60 mt-2 mb-6">
          <span>{fmt(cur)}</span><span>{fmt(dur)}</span>
        </div>

        <div className="flex items-center justify-center gap-8">
          <button data-testid="player-restart-btn" onClick={restart} className="text-white/70"><RotateCcw size={24} /></button>
          <button data-testid="player-play-pause-btn" onClick={toggle} disabled={preparing}
            className="w-20 h-20 rounded-full bg-royal shadow-2xl shadow-royal/40 flex items-center justify-center active:scale-95 transition disabled:opacity-70">
            {preparing ? <Loader2 size={30} className="animate-spin" />
              : playing ? <Pause size={32} fill="white" /> : <Play size={32} fill="white" className="ml-1" />}
          </button>
          <div className="w-6" />
        </div>
        {preparing && <p className="text-center text-white/50 text-xs mt-4">Preparing your narration…</p>}
      </div>

      {showTranscript && (
        <div className="absolute inset-0 z-30 bg-navy/95 backdrop-blur-md flex flex-col" data-testid="transcript-overlay">
          <div className="flex items-center justify-between px-5 pt-6 pb-3 border-b border-white/10">
            <h2 className="font-serif text-xl font-bold">Transcript</h2>
            <button data-testid="transcript-close-btn" onClick={() => setShowTranscript(false)} className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center"><X size={20} /></button>
          </div>
          <div className="flex-1 overflow-y-auto mm-scroll px-5 py-5">
            <TranscriptList cues={cues} transcript={session?.transcript} active={active} onSeek={seek} />
          </div>
        </div>
      )}

      {showFeedback && (
        <div className="absolute inset-0 z-40 bg-black/50 flex items-end" data-testid="feedback-modal">
          <div className="w-full bg-white text-navy rounded-t-3xl p-6 animate-fade-up">
            <div className="w-10 h-1 bg-slate-200 rounded-full mx-auto mb-5" />
            <h3 className="font-serif text-2xl font-bold text-center">How did that feel?</h3>
            <p className="text-slate-500 text-center text-sm mt-1">Your feedback shapes tomorrow's session.</p>
            <div className="grid grid-cols-3 gap-2.5 mt-6">
              {[["🔥", "Locked in"], ["🙂", "Good"], ["😌", "Calm"]].map(([e, l]) => (
                <button key={l} data-testid={`feedback-${l.replace(/\s/g, "-").toLowerCase()}`} onClick={() => sendFeedback(l)}
                  className="flex flex-col items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-2xl py-4 hover:border-royal active:scale-95 transition">
                  <span className="text-2xl">{e}</span>
                  <span className="text-xs font-semibold">{l}</span>
                </button>
              ))}
            </div>
            <button data-testid="feedback-skip-btn" onClick={() => { setShowFeedback(false); leave(); }}
              className="w-full mt-3 text-slate-400 text-sm py-2">Skip</button>
          </div>
        </div>
      )}

      {showUpsell && (
        <UpsellSheet credits={user?.credits || 0} streak={user?.journal_streak || 0}
          onClose={() => { setShowUpsell(false); navigate("/library"); }} />
      )}
    </div>
  );
}
